import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Dumbbell, Activity, Heart, Flame, UserCheck, Compass, Building2, BarChart3,
  CreditCard, MessageCircle, Users, Sparkles, Target, ChevronRight, ChevronLeft, Check,
  PersonStanding, Clock, TrendingUp, HeartPulse, Play, Signal, Wifi, BatteryFull,
} from 'lucide-react';
import { Button, cn } from '@techsofit/ui';
import { Container, Eyebrow } from './components/ui-bits';
import { DEMO_URL, PRICING_URL, APP_ANCHOR, DISCOVER_URL, START_URL, GYM_SIGNUP_URL, TRAINER_SIGNUP_URL, openDemo } from './config';
import { CATEGORIES } from './directory/data';
import { GymPreview, GymOsPreview, TrainerPreview, TrainerOsPreview, DiscoverPreview, ProfileSwitchPreview } from './components/previews';

const go = (url: string) => () => { window.location.href = url; };

// ── Hero (3-pillar slider) ─────────────────────────────────────────────────────

interface Slide {
  tab: string;
  icon: ReactNode;
  title: string;
  body: string;
  primary: { label: string; href: string };
  secondary?: { label: string; href: string };
  visual: ReactNode;
}

const SLIDES: Slide[] = [
  {
    tab: 'Discover',
    icon: <Compass className="size-4" />,
    title: 'Find your fit.',
    body: 'Gyms, courts, classes and coaches near you. Browse real places and people, then book or drop in.',
    primary: { label: 'Explore the directory', href: DISCOVER_URL },
    secondary: { label: 'Get started', href: START_URL },
    visual: <DiscoverPreview />,
  },
  {
    tab: 'Business OS',
    icon: <Building2 className="size-4" />,
    title: 'The operating system for your fitness business.',
    body: 'Members, payments and workouts for your gym, studio or club, with automated text receipts and expiry reminders sent for you.',
    primary: { label: 'Start free trial', href: GYM_SIGNUP_URL },
    secondary: { label: 'See pricing', href: PRICING_URL },
    visual: <GymPreview />,
  },
  {
    tab: 'Personal training',
    icon: <UserCheck className="size-4" />,
    title: 'Coach more, chase less.',
    body: 'Assign programs, track each client’s progress, and let payments and confirmations send themselves.',
    primary: { label: 'Start free trial', href: TRAINER_SIGNUP_URL },
    secondary: { label: 'See pricing', href: PRICING_URL },
    visual: <TrainerPreview />,
  },
];

export function Hero() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef<number | null>(null);

  // Auto-advance slowly. The timer resets whenever `i` changes (so a manual pick
  // gets a full dwell, then auto-advance resumes) and pauses on hover.
  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => setI((p) => (p + 1) % SLIDES.length), 10000);
    return () => clearTimeout(t);
  }, [i, paused]);

  const slide = SLIDES[i];
  const select = (n: number) => setI((n + SLIDES.length) % SLIDES.length);

  return (
    <section
      id="top"
      className="relative overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) select(i + (dx < 0 ? 1 : -1));
        touchX.current = null;
      }}
    >
      <Container className="py-12 md:py-16">
        {/* Tabs — click to switch between the three pillars */}
        <div role="tablist" aria-label="What TechSoFit does" className="flex flex-wrap gap-2">
          {SLIDES.map((s, n) => (
            <button
              key={s.tab}
              type="button"
              role="tab"
              aria-selected={n === i}
              onClick={() => select(n)}
              className={cn(
                'inline-flex items-center gap-2 rounded-pill px-4 py-2 text-sm font-medium transition-colors',
                n === i ? 'bg-ink text-on-dark' : 'bg-card text-ink-soft shadow-card hover:text-ink',
              )}
            >
              {s.icon} {s.tab}
            </button>
          ))}
        </div>

        <div className="mt-8 grid items-center gap-10 md:grid-cols-2 md:gap-8">
          <div key={i} className="animate-fade-up">
            <h1 className="font-display text-display-xl text-ink">{slide.title}</h1>
            <p className="mt-4 max-w-md text-lg text-ink-soft">{slide.body}</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button onClick={go(slide.primary.href)}>{slide.primary.label}</Button>
              {slide.secondary && (
                <a
                  href={slide.secondary.href}
                  className="rounded-pill border border-line px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-card"
                >
                  {slide.secondary.label}
                </a>
              )}
            </div>
            <p className="mt-4 text-sm text-muted">Free for individuals · plans for gyms, clubs &amp; trainers</p>
          </div>
          <div key={`v${i}`} className="animate-fade-up md:pl-6">{slide.visual}</div>
        </div>

        {/* Dots — show all slides; the filled one is current */}
        <div className="mt-8 flex items-center justify-center gap-2.5">
          {SLIDES.map((s, n) => (
            <button
              key={s.tab}
              type="button"
              aria-label={`Go to ${s.tab}`}
              aria-current={n === i}
              onClick={() => select(n)}
              className={cn('size-2.5 rounded-full transition-colors', n === i ? 'bg-ink' : 'bg-ink/25 hover:bg-ink/45')}
            />
          ))}
        </div>
      </Container>

      {/* Arrows — conventional position: sides, vertically centered (desktop) */}
      <button
        type="button"
        aria-label="Previous slide"
        onClick={() => select(i - 1)}
        className="absolute left-3 top-1/2 hidden size-11 -translate-y-1/2 place-items-center rounded-full bg-card text-ink shadow-pop ring-1 ring-black/5 transition-colors hover:bg-surface md:grid lg:left-6"
      >
        <ChevronLeft className="size-5" aria-hidden />
      </button>
      <button
        type="button"
        aria-label="Next slide"
        onClick={() => select(i + 1)}
        className="absolute right-3 top-1/2 hidden size-11 -translate-y-1/2 place-items-center rounded-full bg-card text-ink shadow-pop ring-1 ring-black/5 transition-colors hover:bg-surface md:grid lg:right-6"
      >
        <ChevronRight className="size-5" aria-hidden />
      </button>
    </section>
  );
}

// ── One account, many profiles ────────────────────────────────────────────────

export function OneAccount() {
  const points = [
    'Your personal training, always free',
    'Your gym, if you own or run one',
    'Coaching, if you manage clients',
  ];
  return (
    <section id="one-account" className="border-t border-line/60 py-16 md:py-24">
      <Container className="grid items-center gap-12 md:grid-cols-2">
        <div>
          <Eyebrow>One account</Eyebrow>
          <h2 className="mt-3 font-display text-display-md text-ink">One login. Every side of your fitness.</h2>
          <p className="mt-4 text-lg text-ink-soft">
            Train for yourself, run your gym, and coach clients, all under a single account. Switch
            between them in a tap, the way you switch Slack workspaces. No second login, no juggling.
          </p>
          <ul className="mt-6 flex flex-col gap-3">
            {points.map((p) => (
              <li key={p} className="flex items-center gap-3 text-sm font-medium text-ink">
                <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-accent-soft text-ink"><Check className="size-4" /></span>
                {p}
              </li>
            ))}
          </ul>
          <p className="mt-6 rounded-control bg-surface px-4 py-3 text-sm text-muted">
            A member of a gym? You don’t set anything up. Your gym adds you, and it just shows up in your profile.
          </p>
        </div>
        <div className="md:pl-6">
          <ProfileSwitchPreview />
        </div>
      </Container>
    </section>
  );
}

// ── Discover (directory) ─────────────────────────────────────────────────────

export function Discover() {
  return (
    <section id="discover" className="border-t border-line/60 bg-card/40 py-16 md:py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>Discover</Eyebrow>
          <h2 className="mt-3 font-display text-display-md text-ink">Wherever you want to move.</h2>
          <p className="mt-4 text-lg text-ink-soft">
            Browse real places and people near you. Somewhere to lift, swim, play, stretch
            or train. Find a gym, drop into a class, book a court, or find a coach.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {CATEGORIES.map((c) => (
            <a
              key={c.key}
              href={`/discover/${c.key}`}
              className="group flex items-center gap-3 rounded-card bg-card p-4 shadow-card transition-transform hover:-translate-y-0.5"
            >
              <span className="inline-flex size-10 items-center justify-center rounded-full bg-accent-soft text-ink">
                {c.icon({ className: 'size-5' })}
              </span>
              <span className="text-sm font-medium text-ink">{c.label}</span>
            </a>
          ))}
        </div>

        <div className="mt-8">
          <a
            href="/discover"
            className="inline-flex items-center gap-1 rounded-pill bg-dark px-5 py-2.5 text-sm font-medium text-on-dark transition-opacity hover:opacity-90"
          >
            Browse the directory <ChevronRight className="size-4" />
          </a>
        </div>
      </Container>
    </section>
  );
}

// ── The App ──────────────────────────────────────────────────────────────────

const APP_FEATURES: { title: string; body: string; icon: ReactNode }[] = [
  { title: 'Tick off workouts', body: 'Mark exercises done with a satisfying buzz, and a little confetti when you finish.', icon: <Target className="size-5" /> },
  { title: 'Goals & streaks', body: 'Set goals, keep your streak alive, and see your month at a glance.', icon: <Flame className="size-5" /> },
  { title: 'Your Glow Up', body: 'A shareable recap of how far you’ve come. Your progress, your story.', icon: <Sparkles className="size-5" /> },
  { title: 'With a gym or solo', body: 'Follow your coach’s plan, or build your own. It works either way.', icon: <Compass className="size-5" /> },
];

export function AppShowcase() {
  return (
    <section id="app" className="py-16 md:py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>The App</Eyebrow>
          <h2 className="mt-3 font-display text-display-md text-ink">Your training, leveled up.</h2>
          <p className="mt-4 text-lg text-ink-soft">
            Free for individuals, whether you’re with a gym, a trainer, or going it alone.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {APP_FEATURES.map((f) => (
            <div key={f.title} className="rounded-card bg-card p-5 shadow-card">
              <span className="inline-flex size-10 items-center justify-center rounded-full bg-accent-soft text-ink">
                {f.icon}
              </span>
              <p className="mt-4 font-medium text-ink">{f.title}</p>
              <p className="mt-1 text-sm text-muted">{f.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <p className="font-display text-lg font-semibold text-ink">Free for individuals.</p>
          <span className="rounded-pill bg-surface px-3 py-1.5 text-sm font-medium text-muted">
            iOS &amp; Android coming soon
          </span>
        </div>
      </Container>
    </section>
  );
}

// ── Automated messaging ───────────────────────────────────────────────────────

const TEXTS: { body: ReactNode; time: string }[] = [
  { body: <>Hi Tinashe, we’ve received your payment for your <b>Monthly</b> membership at <b>Iron Fitness Gym</b>. You’re active until 9 July. 💪</>, time: '18:56' },
  { body: <>Your personal-training payment of <b>$20.00</b> with coach Jimmy is received — thank you!</>, time: '18:57' },
  { body: <>New workout from coach Jimmy is ready: <b>Upper Body — Week 3</b>. Open it any time, no login needed.</>, time: '09:12' },
];

/** A phone showing a text (SMS) thread sent automatically from the business, via TechSoFit. */
function TextThreadPhone() {
  return (
    <div className="mx-auto w-full max-w-[290px]">
      <div className="relative rounded-[2.75rem] border-[3px] border-dark-line bg-black p-2 shadow-pop">
        <div className="overflow-hidden rounded-[2.15rem] bg-surface">
          {/* status bar + notch */}
          <div className="relative flex items-center justify-between px-6 pb-1.5 pt-3 text-[11px] font-semibold text-ink">
            <span className="tabular">9:41</span>
            <span className="absolute left-1/2 top-2.5 h-5 w-20 -translate-x-1/2 rounded-full bg-black" aria-hidden />
            <span className="flex items-center gap-1.5">
              <Signal className="size-3.5" aria-hidden />
              <Wifi className="size-3.5" aria-hidden />
              <BatteryFull className="size-4" aria-hidden />
            </span>
          </div>

          {/* conversation header */}
          <div className="flex items-center gap-2.5 border-b border-line px-4 py-3">
            <span className="grid size-9 shrink-0 place-items-center rounded-full bg-accent font-display text-base font-bold text-accent-ink">T</span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-ink">TechSoFit</p>
              <p className="text-xs text-muted">Text message · SMS</p>
            </div>
          </div>

          {/* messages */}
          <div className="flex flex-col gap-3 px-4 py-5">
            {TEXTS.map((t, i) => (
              <div key={i} className="max-w-[86%]">
                <div className="rounded-2xl rounded-bl-md bg-elevated px-3.5 py-2.5 text-sm leading-snug text-ink ring-1 ring-line">
                  {t.body}
                </div>
                <p className="mt-1 pl-1 text-[11px] text-muted">{t.time}</p>
              </div>
            ))}
          </div>

          {/* home indicator */}
          <div className="flex justify-center pb-2.5 pt-1">
            <span className="h-1 w-28 rounded-full bg-line" aria-hidden />
          </div>
        </div>
      </div>
    </div>
  );
}

export function AutomatedMessaging() {
  return (
    <section id="messaging" className="border-t border-line/60 bg-card/40 py-16 md:py-24">
      <Container className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <Eyebrow>Automated messaging</Eyebrow>
          <h2 className="mt-3 font-display text-display-md text-ink">Your members hear from you, automatically.</h2>
          <p className="mt-4 text-lg text-ink-soft">
            When a member pays or their plan is about to expire, TechSoFit sends a text on your
            business’s behalf: receipts, personal-training confirmations and renewal reminders. It
            lands in the one inbox everyone already checks — no app to install, no message for you
            to remember to send.
          </p>
          <p className="mt-6 inline-flex items-center gap-2 rounded-pill bg-surface px-4 py-2 text-sm text-muted">
            <Sparkles className="size-4 text-accent-strong" aria-hidden />
            Included on plans with automated messaging.
          </p>
        </div>
        <div className="lg:pl-6">
          <TextThreadPhone />
        </div>
      </Container>
    </section>
  );
}

// ── Audience split ───────────────────────────────────────────────────────────

const AUDIENCES: { title: string; body: string; href: string; cta: string; tag: 'Free' | 'Plan'; icon: ReactNode }[] = [
  { title: 'Individuals', body: 'Train on your own and track your progress.', href: APP_ANCHOR, cta: 'App coming soon', tag: 'Free', icon: <Sparkles className="size-5" /> },
  { title: 'Gym members', body: 'Your gym runs on TechSoFit, so you get the app.', href: DISCOVER_URL, cta: 'Find a gym', tag: 'Free', icon: <Heart className="size-5" /> },
  { title: 'Personal trainers', body: 'Manage clients, programs and payments.', href: TRAINER_SIGNUP_URL, cta: 'Get started', tag: 'Plan', icon: <UserCheck className="size-5" /> },
  { title: 'Gyms, studios & clubs', body: 'Run members, payments and workouts for your whole business.', href: GYM_SIGNUP_URL, cta: 'Get started', tag: 'Plan', icon: <Building2 className="size-5" /> },
];

export function Audiences() {
  return (
    <section className="border-y border-line/60 bg-card/40 py-16 md:py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>Who it's for</Eyebrow>
          <h2 className="mt-3 font-display text-display-md text-ink">Something for every side of fitness.</h2>
          <p className="mt-4 text-lg text-ink-soft">
            Free for individuals and members. Gyms, studios, clubs and trainers pay only for
            the tools to run their business.
          </p>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {AUDIENCES.map((a) => (
            <a key={a.title} href={a.href} className="group rounded-card bg-card p-6 shadow-card transition-transform hover:-translate-y-0.5">
              <div className="flex items-start justify-between">
                <span className="inline-flex size-11 items-center justify-center rounded-full bg-accent-soft text-ink">
                  {a.icon}
                </span>
                <span className={cn('rounded-pill px-2.5 py-1 text-xs font-semibold', a.tag === 'Free' ? 'bg-accent-soft text-ink' : 'bg-dark text-on-dark')}>
                  {a.tag === 'Free' ? 'Free' : 'Paid plan'}
                </span>
              </div>
              <p className="mt-4 font-display text-lg font-semibold text-ink">{a.title}</p>
              <p className="mt-1 text-sm text-muted">{a.body}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-ink-soft group-hover:text-ink">
                {a.cta} <ChevronRight className="size-4" />
              </span>
            </a>
          ))}
        </div>
      </Container>
    </section>
  );
}

// ── For Gyms (Gym OS) ────────────────────────────────────────────────────────

const GYM_POINTS: { title: string; body: string; icon: ReactNode }[] = [
  { title: 'Payments & renewals', body: 'USD, EcoCash, OneMoney, cash. No more paper notebooks.', icon: <CreditCard className="size-5" /> },
  { title: 'Members & attendance', body: 'Who’s active, who’s lapsing, who trained this week.', icon: <Users className="size-5" /> },
  { title: 'Programs & reminders', body: 'Build workouts; auto text nudges before renewals.', icon: <MessageCircle className="size-5" /> },
  { title: 'Reports at a glance', body: 'Revenue and membership trends, ready to read.', icon: <BarChart3 className="size-5" /> },
];

export function ForGyms() {
  return (
    <section id="for-gyms" className="bg-dark py-16 md:py-24">
      <Container className="grid items-center gap-12 lg:grid-cols-2">
        <div>
          <p className="text-eyebrow uppercase text-accent">Business OS</p>
          <h2 className="mt-3 font-display text-display-md text-on-dark">Run your business. Wow your members.</h2>
          <p className="mt-4 text-lg text-on-dark-muted">
            Everything you need to run a gym, studio, club or academy, plus a beautiful app for
            your members, included free.
          </p>

          <div className="mt-8 grid gap-x-6 gap-y-5 sm:grid-cols-2">
            {GYM_POINTS.map((p) => (
              <div key={p.title} className="flex gap-3">
                <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-accent text-accent-ink">
                  {p.icon}
                </span>
                <div>
                  <p className="font-medium text-on-dark">{p.title}</p>
                  <p className="mt-0.5 text-sm text-on-dark-muted">{p.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Button onClick={go(DEMO_URL)}>Book a demo</Button>
            <a
              href={PRICING_URL}
              className="rounded-pill border border-on-dark/30 px-5 py-2.5 text-sm font-medium text-on-dark transition-colors hover:bg-white/10"
            >
              See pricing
            </a>
          </div>
          <p className="mt-4 text-sm text-on-dark-muted">Built for the way Zimbabwe trains.</p>
        </div>

        <div className="lg:pl-6">
          <GymOsPreview />
        </div>
      </Container>
    </section>
  );
}

// ── For Trainers ─────────────────────────────────────────────────────────────

export function ForTrainers() {
  const points = [
    { t: 'Your clients, organised', i: <Users className="size-5" /> },
    { t: 'Send workout programs', i: <Dumbbell className="size-5" /> },
    { t: 'Get paid your way', i: <CreditCard className="size-5" /> },
    { t: 'Track every check-in', i: <Target className="size-5" /> },
  ];
  return (
    <section id="for-trainers" className="py-16 md:py-24">
      <Container className="grid items-center gap-12 lg:grid-cols-2">
        <div>
          <Eyebrow>For Trainers</Eyebrow>
          <h2 className="mt-3 font-display text-display-md text-ink">Coach anywhere, no gym required.</h2>
          <p className="mt-4 text-lg text-ink-soft">
            Manage your clients, send programs, take payments and track progress. The same
            platform the gyms use, sized for a solo coach.
          </p>
          <div className="mt-8 grid gap-x-6 gap-y-4 sm:grid-cols-2">
            {points.map((x) => (
              <div key={x.t} className="flex items-center gap-3">
                <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-ink">{x.i}</span>
                <span className="text-sm font-medium text-ink">{x.t}</span>
              </div>
            ))}
          </div>
          <div className="mt-9">
            <Button onClick={go(TRAINER_SIGNUP_URL)}>Start as a trainer</Button>
          </div>
        </div>
        <div className="lg:pl-6">
          <TrainerOsPreview />
        </div>
      </Container>
    </section>
  );
}

// ── How it fits together ─────────────────────────────────────────────────────

export function Ecosystem() {
  const steps = [
    { n: '01', t: 'Discover', b: 'Find a gym, court, class or coach near you.', i: <Compass className="size-5" /> },
    { n: '02', t: 'Train', b: 'Track every session, goal and streak in the app.', i: <Activity className="size-5" /> },
    { n: '03', t: 'Powered by gyms, clubs & coaches', b: 'The places and people that keep you moving.', i: <Building2 className="size-5" /> },
  ];
  return (
    <section className="border-t border-line/60 bg-card/40 py-16 md:py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>How it fits together</Eyebrow>
          <h2 className="mt-3 font-display text-display-md text-ink">One platform, the whole journey.</h2>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {steps.map((s) => (
            <div key={s.n} className="rounded-card bg-card p-6 shadow-card">
              <div className="flex items-center justify-between">
                <span className="inline-flex size-10 items-center justify-center rounded-full bg-accent-soft text-ink">{s.i}</span>
                <span className="font-display text-lg font-semibold text-muted">{s.n}</span>
              </div>
              <p className="mt-4 font-medium text-ink">{s.t}</p>
              <p className="mt-1 text-sm text-muted">{s.b}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

// ── Final CTA ────────────────────────────────────────────────────────────────

export function FinalCta() {
  return (
    <section className="py-16 md:py-24">
      <Container>
        <div className="rounded-card bg-accent px-6 py-12 text-center sm:px-12">
          <h2 className="font-display text-display-md text-accent-ink">Ready to find your fit?</h2>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a href={DISCOVER_URL} className="rounded-pill bg-dark px-5 py-2.5 text-sm font-medium text-on-dark transition-opacity hover:opacity-90">
              Explore the directory
            </a>
            <a href={START_URL} className={cn('rounded-pill border border-accent-ink/25 px-5 py-2.5 text-sm font-medium text-accent-ink transition-colors hover:bg-accent-ink/10')}>
              For business
            </a>
          </div>
        </div>
      </Container>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// B2B homepage — "operating system for sports & fitness businesses"
// ═══════════════════════════════════════════════════════════════════════════════

const HERO_VALUES = [
  { icon: <Clock className="size-4" />, label: 'Set up in a day' },
  { icon: <MessageCircle className="size-4" />, label: 'Automated SMS & email' },
  { icon: <TrendingUp className="size-4" />, label: 'Retention & revenue insights' },
  { icon: <Sparkles className="size-4" />, label: 'Built in Zimbabwe' },
];

export function HeroB2B() {
  return (
    <section id="top" className="relative overflow-hidden">
      <Container className="py-14 md:py-20">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-12">
          <div className="min-w-0 animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-pill border border-line bg-card px-3.5 py-1.5 text-sm font-medium text-ink-soft shadow-card">
              <Sparkles className="size-4 text-accent-strong" aria-hidden />
              Technology for the wellness movement
            </span>
            <h1 className="mkt-condensed mt-5 text-[3.25rem] leading-[0.9] text-ink sm:text-[4.5rem] lg:text-[5.25rem]">
              <span className="block">Grow<span className="text-accent-strong">.</span></span>
              <span className="block">Retain<span className="text-accent-strong">.</span></span>
              <span className="block">Belong<span className="text-accent-strong">.</span></span>
            </h1>
            <p className="mt-5 max-w-md text-lg text-ink-soft">
              TechSoFit is the technology behind thriving fitness and wellness businesses across
              Africa — text reminders that bring members back, payments the local way, and a
              growth loop your community loves to share.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button onClick={go(GYM_SIGNUP_URL)}>Start free trial</Button>
              <button
                type="button"
                onClick={openDemo}
                className="inline-flex items-center gap-2 rounded-pill border border-line px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-card"
              >
                <Play className="size-4" aria-hidden /> Book a demo
              </button>
            </div>
            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
              {HERO_VALUES.map((v) => (
                <li key={v.label} className="inline-flex items-center gap-2 text-sm text-muted">
                  <span className="text-ink-soft">{v.icon}</span> {v.label}
                </li>
              ))}
            </ul>
          </div>

          <div className="relative min-w-0 animate-fade-up md:pl-6">
            <div className="overflow-hidden rounded-card shadow-pop">
              <img
                src="/img/hero-banner.webp"
                alt="A member meditating on a rooftop"
                className="aspect-[7/6] w-full object-cover"
                loading="eager"
                width={1400}
                height={1200}
              />
            </div>
            {/* Small product-truth card — this is what the platform actually does, not a stat claim. */}
            <div className="absolute -bottom-4 left-3 flex items-center gap-3 rounded-card bg-card px-4 py-3 shadow-pop ring-1 ring-black/5 sm:-left-4">
              <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-accent text-accent-ink">
                <MessageCircle className="size-5" aria-hidden />
              </span>
              <div className="pr-1">
                <p className="text-sm font-semibold text-ink">Renewal reminder sent</p>
                <p className="text-xs text-muted">By text, automatically</p>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

// ── Value marquee — an ambient strip that signals the movement/community language ──

const MARQUEE_ITEMS = [
  'Retention', 'Community', 'SMS reminders', 'Local payments',
  'Member growth', 'Storytelling', 'Events', 'Belonging',
];

export function Marquee() {
  const row = (
    <div className="tsf-marquee-track items-center" aria-hidden="true">
      {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((label, idx) => (
        <span key={idx} className="flex items-center">
          <span className="mkt-condensed px-6 text-xl text-ink/85 sm:text-2xl">
            {label}
          </span>
          <span className="text-accent-strong" aria-hidden>&bull;</span>
        </span>
      ))}
    </div>
  );
  return (
    <div className="overflow-hidden border-y border-line/60 bg-card/50 py-4">
      {row}
    </div>
  );
}

// ── One platform, every role ─────────────────────────────────────────────────

const ENGINE: { img: string; tag: string; title: string; body: string }[] = [
  { img: '/img/athlete-pullup.webp', tag: 'Retention', title: 'Member growth & retention', body: 'See who’s drifting before they cancel, and win them back.' },
  { img: '/img/coaching.webp', tag: 'Movement', title: 'Workouts & programs', body: 'Build and assign training; members open the plan from a link.' },
  { img: '/img/hero-community.webp', tag: 'Storytelling', title: 'Community & the Glow-Up', body: 'Shareable progress recaps that market your community for you.' },
  { img: '/img/member-phone.webp', tag: 'Nudges', title: 'Everyday reminders', body: 'Automatic text and email nudges — sent for you, on time.' },
  { img: '/img/payments.webp', tag: 'Payments', title: 'Local payments, no friction', body: 'Collect the way your members already pay, and record it in a tap.' },
  { img: '/img/insights.webp', tag: 'Insights', title: 'Business health & insights', body: 'Plain-language signals on what’s working and what needs you.' },
  { img: '/img/community-band.webp', tag: 'Events', title: 'Events & guest passes', body: 'Run events, invite guests, and turn every visitor into a lead.' },
  { img: '/img/nutrition.webp', tag: 'Content', title: 'Recipes & learn', body: 'Nutrition and wellness content that keeps members engaged.' },
];

export function Roles() {
  return (
    <section id="features" className="border-y border-line/60 bg-surface/40 py-16 md:py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>What we power</Eyebrow>
          <h2 className="mt-3 font-display text-display-md text-ink">The engine under your community.</h2>
          <p className="mt-4 text-lg text-ink-soft">You bring the heart. TechSoFit runs everything that keeps people showing up — and coming back.</p>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {ENGINE.map((e) => (
            <article key={e.title} className="overflow-hidden rounded-card bg-card shadow-card ring-1 ring-line">
              <div className="relative h-24">
                <img src={e.img} alt="" loading="lazy" className="absolute inset-0 size-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" aria-hidden />
              </div>
              <div className="p-5">
                <p className="text-eyebrow uppercase text-accent-strong">{e.tag}</p>
                <p className="mt-1.5 font-display text-lg font-semibold text-ink">{e.title}</p>
                <p className="mt-1 text-sm text-muted">{e.body}</p>
              </div>
            </article>
          ))}
        </div>
      </Container>
    </section>
  );
}

// ── Built for every industry ─────────────────────────────────────────────────

const INDUSTRIES: { title: string; body: string; icon: ReactNode }[] = [
  { title: 'Gyms', body: 'Memberships, check-ins, renewals and staff — all in one place.', icon: <Dumbbell className="size-5" /> },
  { title: 'Studios', body: 'Class schedules, packages and the members who keep coming back.', icon: <PersonStanding className="size-5" /> },
  { title: 'Personal trainers', body: 'Coach clients, take payments and track results — no gym needed.', icon: <UserCheck className="size-5" /> },
  { title: 'Run clubs', body: 'Events, sign-ups and recaps that turn a run into a community.', icon: <Activity className="size-5" /> },
  { title: 'Wellness communities', body: 'Directory, recipes, stories and events — powered end to end.', icon: <Heart className="size-5" /> },
  { title: 'Corporate wellness', body: 'Keep teams moving with programs, nudges and simple reporting.', icon: <Building2 className="size-5" /> },
];

export function Industries() {
  return (
    <section id="industries" className="py-16 md:py-24">
      <Container>
        <div className="max-w-3xl">
          <Eyebrow>Built for how you gather</Eyebrow>
          <h2 className="mt-3 font-display text-display-md text-ink">Wellness isn&rsquo;t one-size-fits-all. Your software shouldn&rsquo;t be either.</h2>
          <p className="mt-4 text-lg text-ink-soft">It looks different for every community. One platform that flexes to the shape of yours — whoever you gather and however you move.</p>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {INDUSTRIES.map((it) => (
            <div key={it.title} className="rounded-card bg-card p-6 shadow-card ring-1 ring-line">
              <span className="inline-flex size-11 items-center justify-center rounded-2xl bg-accent-soft text-accent-strong">
                {it.icon}
              </span>
              <p className="mt-4 font-display text-lg font-semibold text-ink">{it.title}</p>
              <p className="mt-1 text-sm text-muted">{it.body}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

// ── Showcase — photo strip that ties real moments to what the product does ────

const SHOWCASE: { img: string; alt: string; tag: string; title: string; body: string }[] = [
  {
    img: '/img/coaching.webp',
    alt: 'A coach guiding a member through a lift',
    tag: 'Workouts & coaching',
    title: 'Programs that travel with your members',
    body: 'Build workouts and assign them; members open the plan from a link — no login, no friction.',
  },
  {
    img: '/img/member-phone.webp',
    alt: 'A member using the app on their phone',
    tag: 'A member app they’ll open',
    title: 'Their training, in their pocket',
    body: 'Check-ins, goals, streaks and receipts in a branded app — free for every member you add.',
  },
  {
    img: '/img/nutrition.webp',
    alt: 'A colourful, healthy meal bowl',
    tag: 'Recipes & content',
    title: 'Keep people engaged between sessions',
    body: 'Nutrition and wellness content that keeps your community coming back, not just checking in.',
  },
];

export function Showcase() {
  return (
    <section className="py-16 md:py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>In the wild</Eyebrow>
          <h2 className="mt-3 font-display text-display-md text-ink">More than software. A reason to come back.</h2>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SHOWCASE.map((s) => (
            <article key={s.title} className="group overflow-hidden rounded-card bg-card shadow-card transition-transform hover:-translate-y-1">
              <div className="overflow-hidden">
                <img
                  src={s.img}
                  alt={s.alt}
                  loading="lazy"
                  className="aspect-[5/4] w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                />
              </div>
              <div className="p-5">
                <p className="text-eyebrow uppercase text-accent-strong">{s.tag}</p>
                <p className="mt-2 font-display text-lg font-semibold text-ink">{s.title}</p>
                <p className="mt-1 text-sm text-muted">{s.body}</p>
              </div>
            </article>
          ))}
        </div>
      </Container>
    </section>
  );
}

// ── Why businesses choose us (dark band + CTA) ───────────────────────────────

const WHY: { title: string; body: string; icon: ReactNode }[] = [
  { title: 'Save time', body: 'Automate reminders, receipts and admin so your team does less busywork.', icon: <Clock className="size-5" /> },
  { title: 'Increase revenue', body: 'Convert more leads and keep more members paying, month after month.', icon: <TrendingUp className="size-5" /> },
  { title: 'Improve retention', body: 'Member-health flags tell you who is about to leave, before they do.', icon: <HeartPulse className="size-5" /> },
  { title: 'Decide with data', body: 'A business health score, trends and insights, no spreadsheets required.', icon: <BarChart3 className="size-5" /> },
];

export function WhyChoose() {
  return (
    <section className="bg-dark py-16 md:py-24">
      <Container>
        <div className="max-w-2xl">
          <p className="text-eyebrow uppercase text-accent">Why TechSoFit</p>
          <h2 className="mt-3 font-display text-display-md text-on-dark">More members, more revenue, less admin.</h2>
        </div>
        <div className="mt-10 grid gap-x-8 gap-y-8 sm:grid-cols-2">
          {WHY.map((w) => (
            <div key={w.title} className="flex gap-4">
              <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-2xl bg-accent text-accent-ink">
                {w.icon}
              </span>
              <div>
                <p className="font-display text-lg font-semibold text-on-dark">{w.title}</p>
                <p className="mt-1 text-sm text-on-dark-muted">{w.body}</p>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

// ── Community band — photo-backed closing CTA ("Go beyond limits") ────────────

export function CommunityBand() {
  return (
    <section className="py-16 md:py-24">
      <Container>
        <div className="relative overflow-hidden rounded-card shadow-pop">
          <img
            src="/img/band-ropes.webp"
            alt="An athlete training with battle ropes"
            loading="lazy"
            className="absolute inset-0 size-full object-cover object-right"
          />
          {/* Legibility wash — strong on the left (text) since the photo is bright there. */}
          <div className="absolute inset-0 bg-gradient-to-r from-dark/95 via-dark/85 to-dark/45" aria-hidden />
          <div className="absolute inset-0 bg-dark/30" aria-hidden />
          <div className="relative px-6 py-16 sm:px-12 md:py-20">
            <div className="max-w-xl">
              <p className="text-eyebrow uppercase text-accent">Join the movement</p>
              <h2 className="mt-3 font-display text-display-md text-on-dark">Go beyond limits.</h2>
              <p className="mt-4 text-lg text-white/85">
                Built with and for African fitness and wellness businesses. Start free, keep your
                members close, and grow a community that lasts.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href={GYM_SIGNUP_URL} className="rounded-pill bg-accent px-5 py-2.5 text-sm font-medium text-accent-ink transition-transform hover:-translate-y-0.5">
                  Start free trial
                </a>
                <button type="button" onClick={openDemo} className="rounded-pill border border-on-dark/30 px-5 py-2.5 text-sm font-medium text-on-dark transition-colors hover:bg-white/10">
                  Book a demo
                </button>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
